import { useDebouncedCallback } from "@/app/hooks/use-debounced-callback"
import { CloseIcon } from "@/app/icons/close"
import { SearchIcon } from "@/app/icons/search"
import * as amplitude from "@amplitude/analytics-browser"
import { useLingui } from "@lingui/react/macro"
import { Button, Input, cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { NftFiltersMoleculeReatom } from "../../filters/store.reatom"

interface SearchProps {
  placeholder?: string
  className?: string
}

const MAX_SEARCH_LENGTH = 100

export const CollectionSearch = reatomComponent<SearchProps>(
  ({ placeholder, className }) => {
    const { t } = useLingui()

    const { textFilter, resetTextFilter } = useMolecule(
      NftFiltersMoleculeReatom,
    )
    const text = textFilter()
    const [value, setValue] = useState(text || "")
    const searchInputRef = useRef<HTMLInputElement>(null)

    const placeholderText = placeholder ?? t`Search items`

    const debouncedSearch = useDebouncedCallback((query: string) => {
      textFilter.set(query || undefined)
      amplitude.track("Search items", { query })
    }, 500)

    const handleClearSearch = useCallback(() => {
      setValue("")
      resetTextFilter()
      searchInputRef.current?.focus()
    }, [resetTextFilter])

    const handleEnter = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === "Enter" && value.trim()) {
          e.preventDefault()
          debouncedSearch(value.trim())
        }
      },
      [value, debouncedSearch],
    )

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        handleEnter(e)
      }

      document.addEventListener("keydown", handleKeyDown)

      return () => {
        document.removeEventListener("keydown", handleKeyDown)
      }
    }, [handleEnter])

    useEffect(() => {
      setValue(text || "")
    }, [text])

    const sanitizeQuery = (query: string) => {
      return query.slice(0, MAX_SEARCH_LENGTH)
    }

    const handleSearch = (query: string) => {
      const sanitizedQuery = sanitizeQuery(query)
      setValue(sanitizedQuery)
      debouncedSearch(sanitizedQuery)
    }

    return (
      <div
        className={cn(
          "relative flex h-9 w-full items-center gap-1 rounded-md border border-foreground/[0.1] bg-transparent px-2 transition-all duration-200 focus-within:ring-1 focus-within:ring-brand",
          className,
        )}
      >
        <SearchIcon className="h-6 w-6 shrink-0 text-foreground/40" />
        <Input
          ref={searchInputRef}
          onChange={(e) => handleSearch(e.target.value)}
          value={value}
          name="collection-search"
          type="search"
          placeholder={placeholderText}
          autoComplete="off"
          aria-label={placeholderText}
          data-testid="collection-search-input"
          className="w-full border-none bg-transparent p-0 text-sm outline-none placeholder:text-foreground/40 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none"
        />
        {value && (
          <Button
            variant="secondary"
            onClick={handleClearSearch}
            className="absolute top-2 right-2 h-5 w-5 rounded-full p-1 text-foreground/40 [&_svg]:size-2"
          >
            <CloseIcon />
          </Button>
        )}
      </div>
    )
  },
  "CollectionSearch",
)
